import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Warehouse, Building, Package, PackagePlus, PackageMinus, 
  AlertTriangle, Truck, Eye, FileText, Calendar, Clock, User,
  Boxes, Search, AlertCircle, CheckCircle, X, Download, Filter, Building2,
  Plus, Upload, Trash2, Layers, ArrowUp, ArrowDown, ArrowUpDown, Pencil, Phone, MapPin,
  ChevronDown, ChevronRight, ShoppingCart, DollarSign, TrendingUp,
  UserCheck, History, Info, Wrench, HelpCircle, ArrowLeftRight, RefreshCw, Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Textarea } from '../../components/ui/textarea';
import PurchasingStock from './PurchasingStock';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PurchasingWarehouses = ({ readOnly = false }) => {
  const location = useLocation();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [activeMainTab, setActiveMainTab] = useState('stok-genel'); // For top-level tabs
  const [activeSubTab, setActiveSubTab] = useState('giris');
  const [loading, setLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(true);
  
  // Data states for each sub-tab
  const [stokGirisleri, setStokGirisleri] = useState([]);
  const [stokCikislari, setStokCikislari] = useState([]);
  const [depoStok, setDepoStok] = useState([]);
  const [stokKartlari, setStokKartlari] = useState([]);
  const [gelecekUrunler, setGelecekUrunler] = useState([]);
  const [hataliUrunler, setHataliUrunler] = useState([]);
  
  // Zimmetler, Taşeronlar, Teslim Alanlar States
  const [zimmetler, setZimmetler] = useState([]);
  const [zimmetlerLoading, setZimmetlerLoading] = useState(false);
  const [taseronlar, setTaseronlar] = useState([]);
  const [taseronlarLoading, setTaseronlarLoading] = useState(false);
  const [teslimAlanlar, setTeslimAlanlar] = useState([]);
  const [teslimAlanlarLoading, setTeslimAlanlarLoading] = useState(false);
  
  // Zimmetler extended states
  const [zimmetSearchTerm, setZimmetSearchTerm] = useState('');
  const [zimmetSortConfig, setZimmetSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [showZimmetInfo, setShowZimmetInfo] = useState(() => {
    return localStorage.getItem('purchasing_zimmet_info_hidden') !== 'true';
  });
  const [selectedZimmetGecmis, setSelectedZimmetGecmis] = useState(null);
  const [zimmetGecmisi, setZimmetGecmisi] = useState([]);
  const [showGecmisModal, setShowGecmisModal] = useState(false);
  
  // Taşeronlar extended states
  const [selectedTaseron, setSelectedTaseron] = useState(null);
  const [taseronSearchTerm, setTaseronSearchTerm] = useState('');
  const [taseronMalzemeleri, setTaseronMalzemeleri] = useState([]);
  const [taseronMalzemeLoading, setTaseronMalzemeLoading] = useState(false);
  const [showTaseronInfo, setShowTaseronInfo] = useState(() => {
    return localStorage.getItem('purchasing_taseron_info_hidden') !== 'true';
  });
  
  // Teslim Alanlar extended states
  const [selectedTeslimAlan, setSelectedTeslimAlan] = useState(null);
  const [teslimAlanSearchTerm, setTeslimAlanSearchTerm] = useState('');
  const [showTeslimAlanlarInfo, setShowTeslimAlanlarInfo] = useState(() => {
    return localStorage.getItem('purchasing_teslim_alanlar_info_hidden') !== 'true';
  });
  
  // Transfer states
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showTransferHistoryModal, setShowTransferHistoryModal] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [transferForm, setTransferForm] = useState({
    source_branch_id: '',
    destination_branch_id: '',
    items: [],
    description: '',
    teslim_alan_belgesi: null,
    teslim_eden_belgesi: null
  });
  const [sourceStock, setSourceStock] = useState([]);
  const [sourceStockLoading, setSourceStockLoading] = useState(false);
  const [selectedTransferItems, setSelectedTransferItems] = useState([]);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [teslimAlanUploading, setTeslimAlanUploading] = useState(false);
  const [teslimEdenUploading, setTeslimEdenUploading] = useState(false);
  
  // Cari Hesap States
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [cariSearch, setCariSearch] = useState('');
  const [cariSort, setCariSort] = useState({ field: 'name', direction: 'asc' });
  const [expandedCari, setExpandedCari] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});
  
  // Stok Kartları States (global)
  const [globalStokKartlari, setGlobalStokKartlari] = useState([]);
  const [stokKartSearch, setStokKartSearch] = useState('');
  const [stokKartSort, setStokKartSort] = useState({ field: 'product_name', direction: 'asc' });
  
  // Filter states
  const [stokSearch, setStokSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    product: ''
  });
  
  // Bulk selection states
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  
  // Detail modal states
  const [detailModal, setDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [productHistoryModal, setProductHistoryModal] = useState(false);
  const [selectedProductHistory, setSelectedProductHistory] = useState({ product_name: '', entries: [] });

  // Stok Ekle Modal States
  const [stokEkleModal, setStokEkleModal] = useState(false);
  const [stokEkleForm, setStokEkleForm] = useState({
    target_branch_id: '',
    cari_hesap_id: '',
    entry_date: '',
    entry_time: '',
    estimated_arrival_date: '',
    estimated_arrival_time: '',
    items: [{ stok_karti_id: '', quantity: '', unit: 'adet', purchase_price: '' }],
    documents: [],
    description: ''
  });
  const [cariSearchInput, setCariSearchInput] = useState('');
  const [filteredCari, setFilteredCari] = useState([]);
  const [showCariDropdown, setShowCariDropdown] = useState(false);
  const [selectedCariName, setSelectedCariName] = useState('');
  const [stokSearchInputs, setStokSearchInputs] = useState(['']);
  const [filteredStokList, setFilteredStokList] = useState([[]]);
  const [showStokDropdowns, setShowStokDropdowns] = useState([false]);
  const [stokEkleSubmitting, setStokEkleSubmitting] = useState(false);

  // Sorting states for each table
  const [girisSort, setGirisSort] = useState({ key: 'entry_date', direction: 'desc' });
  const [cikisSort, setCikisSort] = useState({ key: 'created_at', direction: 'desc' });
  const [depoStokSort, setDepoStokSort] = useState({ key: 'product_name', direction: 'asc' });

  // Generic sort function
  const sortData = (data, sortConfig) => {
    if (!sortConfig.key || !data) return data;
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle dates
      if (sortConfig.key.includes('date') || sortConfig.key.includes('_at')) {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      }
      // Handle numbers
      else if (typeof aValue === 'number' || !isNaN(parseFloat(aValue))) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      // Handle strings
      else {
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle sort click
  const handleSort = (setSortFn, currentSort, key) => {
    setSortFn({
      key,
      direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  // Sortable Header Component
  const SortableHeader = ({ children, sortKey, currentSort, onSort }) => {
    const isActive = currentSort.key === sortKey;
    return (
      <TableHead 
        className="cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => onSort(sortKey)}
      >
        <div className="flex items-center gap-1">
          {children}
          {isActive ? (
            currentSort.direction === 'asc' ? 
              <ArrowUp className="h-3 w-3 text-orange-600" /> : 
              <ArrowDown className="h-3 w-3 text-orange-600" />
          ) : (
            <ArrowUpDown className="h-3 w-3 text-gray-400" />
          )}
        </div>
      </TableHead>
    );
  };

  // Excel Export Function
  const exportToExcel = (data, filename, columns) => {
    if (!data || data.length === 0) {
      toast.error('İndirilecek veri bulunamadı');
      return;
    }
    
    const exportData = data.map(item => {
      const row = {};
      columns.forEach(col => {
        let value = item[col.key];
        // Format dates
        if (col.key.includes('date') || col.key.includes('_at')) {
          value = value ? new Date(value).toLocaleDateString('tr-TR') : '-';
        }
        // Format numbers
        if (col.type === 'number') {
          value = parseFloat(value) || 0;
        }
        row[col.label] = value ?? '-';
      });
      return row;
    });
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Veri');
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel dosyası indirildi');
  };

  // Bulk selection functions
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleSelectAll = (items) => {
    const itemIds = items.map(i => i.id);
    const allSelected = itemIds.every(id => selectedItems.includes(id));
    
    if (allSelected) {
      setSelectedItems(prev => prev.filter(id => !itemIds.includes(id)));
    } else {
      setSelectedItems(prev => [...new Set([...prev, ...itemIds])]);
    }
  };

  const handleBulkDeleteStock = async (branchId) => {
    if (selectedItems.length === 0) {
      toast.error('Lütfen silmek için kayıt seçin');
      return;
    }
    
    if (!window.confirm(`${selectedItems.length} stok kaydını kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!`)) {
      return;
    }
    
    setBulkDeleteLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/depot/stock/bulk-delete`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stock_ids: selectedItems, branch_id: branchId })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.deleted_count || selectedItems.length} kayıt silindi`);
        setSelectedItems([]);
        // Refresh data
        if (branchId && selectedBranch) {
          fetchDepoStok(branchId);
          fetchStokGirisleri(branchId);
          fetchStokCikislari(branchId);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Kayıtlar silinemedi');
      }
    } catch (error) {
      console.error('Toplu silme hatası:', error);
      toast.error('Kayıtlar silinirken hata oluştu');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Fetch branches
  const fetchBranches = useCallback(async () => {
    setBranchesLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/purchasing/branches-with-depot`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
        // Stok tabı varsayılan olarak açık kalacak, şube seçilmeyecek
      }
    } catch (error) {
      console.error('Şubeler yüklenirken hata:', error);
      toast.error('Şubeler yüklenemedi');
    } finally {
      setBranchesLoading(false);
    }
  }, []);

  // Fetch Global Stok Kartları
  const fetchGlobalStokKartlari = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/global/stok-kartlari`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGlobalStokKartlari(data);
      }
    } catch (error) {
      console.error('Stok kartları yüklenirken hata:', error);
    }
  }, []);

  // Fetch depot data for selected branch
  const fetchDepotData = useCallback(async () => {
    if (!selectedBranch) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const branchId = selectedBranch.id;
      
      // Fetch all data in parallel - use global endpoint for stok kartlari
      const [girisRes, cikisRes, stokRes, kartRes, gelecekRes, hataliRes, zimmetRes, taseronRes, teslimRes] = await Promise.all([
        fetch(`${API_URL}/api/depot/stok-girisleri?branch_id=${branchId}`, { headers }),
        fetch(`${API_URL}/api/depot/stok-cikisleri?branch_id=${branchId}`, { headers }),
        fetch(`${API_URL}/api/depot/stok-all?branch_id=${branchId}`, { headers }),
        fetch(`${API_URL}/api/global/stok-kartlari`, { headers }),  // Global stok kartları
        fetch(`${API_URL}/api/depot/gelecek-urunler?branch_id=${branchId}`, { headers }),
        fetch(`${API_URL}/api/depot/hatali-urunler?branch_id=${branchId}`, { headers }),
        fetch(`${API_URL}/api/depot/zimmetler?branch_id=${branchId}`, { headers }),
        fetch(`${API_URL}/api/depot/taseronlar?branch_id=${branchId}`, { headers }),
        fetch(`${API_URL}/api/depot/teslim-alanlar?branch_id=${branchId}`, { headers })
      ]);
      
      if (girisRes.ok) setStokGirisleri(await girisRes.json());
      if (cikisRes.ok) setStokCikislari(await cikisRes.json());
      if (stokRes.ok) setDepoStok(await stokRes.json());
      if (kartRes.ok) setStokKartlari(await kartRes.json());
      if (gelecekRes.ok) setGelecekUrunler(await gelecekRes.json());
      if (hataliRes.ok) setHataliUrunler(await hataliRes.json());
      if (zimmetRes.ok) setZimmetler(await zimmetRes.json());
      if (taseronRes.ok) setTaseronlar(await taseronRes.json());
      if (teslimRes.ok) setTeslimAlanlar(await teslimRes.json());
      
    } catch (error) {
      console.error('Depo verileri yüklenirken hata:', error);
      toast.error('Depo verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [selectedBranch]);

  useEffect(() => {
    fetchBranches();
    fetchGlobalStokKartlari();
  }, [fetchBranches, fetchGlobalStokKartlari]);

  useEffect(() => {
    if (selectedBranch) {
      fetchDepotData();
    }
  }, [selectedBranch, fetchDepotData]);

  // Fetch Cari Hesaplar for Stok Ekle (Global)
  const fetchCariHesaplar = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/global/cari-hesaplar`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCariHesaplar(data);
      }
    } catch (error) {
      console.error('Cari hesaplar yüklenemedi:', error);
    }
  }, []);

  useEffect(() => {
    fetchCariHesaplar();
  }, [fetchCariHesaplar]);

  // Cari Hesap search filter
  useEffect(() => {
    if (cariSearchInput.trim()) {
      const filtered = cariHesaplar.filter(c => 
        c.name?.toLowerCase().includes(cariSearchInput.toLowerCase())
      );
      setFilteredCari(filtered);
    } else {
      setFilteredCari(cariHesaplar);
    }
  }, [cariSearchInput, cariHesaplar]);

  // Stok Kartı search filter
  const handleStokSearch = (index, value) => {
    const newInputs = [...stokSearchInputs];
    newInputs[index] = value;
    setStokSearchInputs(newInputs);

    const newFiltered = [...filteredStokList];
    if (value.trim()) {
      newFiltered[index] = stokKartlari.filter(s =>
        s.product_name?.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      newFiltered[index] = stokKartlari;
    }
    setFilteredStokList(newFiltered);
  };

  // Select Cari Hesap
  const handleSelectCari = (cari) => {
    setStokEkleForm(prev => ({ ...prev, cari_hesap_id: cari.id }));
    setSelectedCariName(cari.name);
    setCariSearchInput(cari.name);
    setShowCariDropdown(false);
  };

  // Select Stok Kartı
  const handleSelectStok = (index, stok) => {
    const newItems = [...stokEkleForm.items];
    newItems[index] = { ...newItems[index], stok_karti_id: stok.id, unit: stok.unit || 'adet' };
    setStokEkleForm(prev => ({ ...prev, items: newItems }));
    
    const newInputs = [...stokSearchInputs];
    newInputs[index] = stok.product_name;
    setStokSearchInputs(newInputs);
    
    const newDropdowns = [...showStokDropdowns];
    newDropdowns[index] = false;
    setShowStokDropdowns(newDropdowns);
  };

  // Add new item row
  const handleAddItem = () => {
    setStokEkleForm(prev => ({
      ...prev,
      items: [...prev.items, { stok_karti_id: '', quantity: '', unit: 'adet', purchase_price: '' }]
    }));
    setStokSearchInputs(prev => [...prev, '']);
    setFilteredStokList(prev => [...prev, stokKartlari]);
    setShowStokDropdowns(prev => [...prev, false]);
  };

  // Update item purchase price
  const handleItemPriceChange = (index, value) => {
    const newItems = [...stokEkleForm.items];
    newItems[index] = { ...newItems[index], purchase_price: value };
    setStokEkleForm(prev => ({ ...prev, items: newItems }));
  };

  // Remove item row
  const handleRemoveItem = (index) => {
    if (stokEkleForm.items.length <= 1) return;
    setStokEkleForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
    setStokSearchInputs(prev => prev.filter((_, i) => i !== index));
    setFilteredStokList(prev => prev.filter((_, i) => i !== index));
    setShowStokDropdowns(prev => prev.filter((_, i) => i !== index));
  };

  // Update item quantity
  const handleItemQuantityChange = (index, value) => {
    const newItems = [...stokEkleForm.items];
    newItems[index] = { ...newItems[index], quantity: value };
    setStokEkleForm(prev => ({ ...prev, items: newItems }));
  };

  // Handle file upload for Stok Ekle
  const handleStokFileChange = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setStokEkleForm(prev => ({
          ...prev,
          documents: [...prev.documents, { name: file.name, data: reader.result, type: file.type }]
        }));
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  // Remove file from Stok Ekle
  const handleRemoveStokFile = (index) => {
    setStokEkleForm(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Open Stok Ekle Modal
  const openStokEkleModal = () => {
    // Get current date and time
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().slice(0, 5);
    
    setStokEkleForm({
      target_branch_id: selectedBranch?.id || '',
      cari_hesap_id: '',
      entry_date: currentDate,
      entry_time: currentTime,
      estimated_arrival_date: '',
      estimated_arrival_time: '',
      items: [{ stok_karti_id: '', quantity: '', unit: 'adet', purchase_price: '' }],
      documents: [],
      description: ''
    });
    setSelectedCariName('');
    setCariSearchInput('');
    setStokSearchInputs(['']);
    setFilteredStokList([stokKartlari]);
    setShowStokDropdowns([false]);
    setStokEkleModal(true);
  };

  // Submit Stok Ekle
  const handleSubmitStokEkle = async () => {
    // Şube seçimi kontrolü
    if (!stokEkleForm.target_branch_id) {
      toast.error('Lütfen bir şube seçiniz');
      return;
    }
    
    if (!stokEkleForm.cari_hesap_id) {
      toast.error('Cari hesap seçiniz');
      return;
    }
    
    // Tahmini geliş tarihi kontrolü
    if (!stokEkleForm.estimated_arrival_date) {
      toast.error('Tahmini depoya geliş tarihi giriniz');
      return;
    }
    
    const validItems = stokEkleForm.items.filter(item => item.stok_karti_id && item.quantity);
    if (validItems.length === 0) {
      toast.error('En az bir ürün ekleyiniz');
      return;
    }

    // Belge zorunlu kontrolü
    if (stokEkleForm.documents.length === 0) {
      toast.error('En az bir belge yüklemeniz gerekmektedir');
      return;
    }

    setStokEkleSubmitting(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Get target branch info
      const targetBranch = branches.find(b => b.id === stokEkleForm.target_branch_id);
      
      // Map items with product names and purchase price
      const itemsWithNames = validItems.map(item => {
        const stokKarti = stokKartlari.find(s => s.id === item.stok_karti_id);
        return {
          ...item,
          product_name: stokKarti?.product_name || '',
          purchase_price: item.purchase_price ? parseFloat(item.purchase_price) : null
        };
      });

      const payload = {
        ...stokEkleForm,
        items: itemsWithNames,
        branch_id: stokEkleForm.target_branch_id,
        branch_name: targetBranch?.name || '',
        cari_hesap_name: selectedCariName,
        entry_datetime: `${stokEkleForm.entry_date}T${stokEkleForm.entry_time}:00`,
        estimated_arrival_datetime: stokEkleForm.estimated_arrival_time 
          ? `${stokEkleForm.estimated_arrival_date}T${stokEkleForm.estimated_arrival_time}:00`
          : `${stokEkleForm.estimated_arrival_date}T12:00:00`,
        add_to_gelecek_urunler: true  // Gelecek ürünlere ekle flag'i
      };

      const response = await fetch(`${API_URL}/api/depot/stok-girisi`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Stok girişi başarıyla eklendi ve şube deposuna bildirildi');
        setStokEkleModal(false);
        fetchDepotData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Stok girişi eklenemedi');
      }
    } catch (error) {
      console.error('Stok ekle hatası:', error);
      toast.error('Stok girişi eklenirken hata oluştu');
    } finally {
      setStokEkleSubmitting(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ dateFrom: '', dateTo: '', product: '' });
    setStokSearch('');
  };

  const hasActiveFilters = filters.dateFrom || filters.dateTo || filters.product || stokSearch;

  // Get unique products from stok cikislari
  const uniqueProducts = useMemo(() => {
    const products = new Set();
    stokCikislari.forEach(c => {
      c.items?.forEach(item => {
        if (item.product_name) products.add(item.product_name);
      });
    });
    return Array.from(products);
  }, [stokCikislari]);

  // Filter stok cikislari
  const filteredCikislar = useMemo(() => {
    return stokCikislari.filter(cikis => {
      if (filters.dateFrom) {
        const cikisDate = new Date(cikis.created_at).setHours(0,0,0,0);
        const fromDate = new Date(filters.dateFrom).setHours(0,0,0,0);
        if (cikisDate < fromDate) return false;
      }
      if (filters.dateTo) {
        const cikisDate = new Date(cikis.created_at).setHours(0,0,0,0);
        const toDate = new Date(filters.dateTo).setHours(0,0,0,0);
        if (cikisDate > toDate) return false;
      }
      if (filters.product) {
        const hasProduct = cikis.items?.some(item => item.product_name === filters.product);
        if (!hasProduct) return false;
      }
      return true;
    });
  }, [stokCikislari, filters]);

  // Calculate totals for stok
  const calculateTotals = (productName) => {
    let totalGiris = 0;
    let totalCikis = 0;
    
    stokGirisleri.forEach(giris => {
      giris.items?.forEach(item => {
        if (item.product_name === productName) {
          totalGiris += parseFloat(item.quantity) || 0;
        }
      });
    });
    
    stokCikislari.forEach(cikis => {
      cikis.items?.forEach(item => {
        if (item.product_name === productName) {
          totalCikis += parseFloat(item.quantity) || 0;
        }
      });
    });
    
    return { totalGiris, totalCikis };
  };

  // Enrich stok data
  const enrichedStok = depoStok.map(s => {
    const kart = stokKartlari.find(k => k.id === s.stok_karti_id);
    const { totalGiris, totalCikis } = calculateTotals(s.product_name);
    return {
      ...s,
      minimum_stock: kart?.minimum_stock || 0,
      total_giris: totalGiris,
      total_cikis: totalCikis
    };
  });

  const filteredStok = enrichedStok.filter(s => 
    s.product_name?.toLowerCase().includes(stokSearch.toLowerCase())
  );

  const getStockStatus = (quantity, minimumStock) => {
    const minStock = minimumStock || 0;
    if (quantity <= 0) {
      return { label: 'Tükendi', className: 'bg-red-100 text-red-700' };
    }
    if (quantity <= minStock) {
      return { label: 'Kritik Seviye', className: 'bg-red-100 text-red-700' };
    }
    if (quantity <= minStock * 1.5) {
      return { label: 'Azalıyor', className: 'bg-amber-100 text-amber-700' };
    }
    return { label: 'Yeterli', className: 'bg-emerald-100 text-emerald-700' };
  };

  // Stock summary stats
  const totalItems = enrichedStok.length;
  const lowStockItems = enrichedStok.filter(s => s.quantity > 0 && s.quantity <= (s.minimum_stock || 0)).length;
  const outOfStockItems = enrichedStok.filter(s => s.quantity <= 0).length;

  // Status badges
  const getStatusBadge = (status) => {
    switch (status) {
      case 'teslim_alindi':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="h-3 w-3 mr-1" />Teslim Alındı</Badge>;
      case 'iptal':
        return <Badge variant="destructive"><X className="h-3 w-3 mr-1" />İptal</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />Bekliyor</Badge>;
    }
  };

  const getIssueTypeBadge = (type) => {
    switch (type) {
      case 'eksik':
        return <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="h-3 w-3 mr-1" />Eksik Ürün</Badge>;
      case 'hasarli':
        return <Badge className="bg-orange-100 text-orange-700"><Package className="h-3 w-3 mr-1" />Hasarlı Ürün</Badge>;
      default:
        return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Hatalı Ürün</Badge>;
    }
  };

  const getDefectiveStatusBadge = (status) => {
    if (status === 'cozuldu') {
      return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="h-3 w-3 mr-1" />Çözüldü</Badge>;
    }
    return <Badge className="bg-red-100 text-red-700"><Clock className="h-3 w-3 mr-1" />Açık</Badge>;
  };

  const getHataCikmaZamaniLabel = (value) => {
    const labels = {
      'teslim_alirken': 'Ürün teslim alırken',
      'paket_acilinca': 'Ürün paketi açılınca',
      'uygulama_esnasinda': 'Ürün uygulama esnasında',
      'uygulama_sonrasi': 'Uygulama sonrası'
    };
    return labels[value] || value || '-';
  };

  // Detail modal handlers
  const openDetailModal = (data) => {
    setDetailData(data);
    setDetailModal(true);
  };

  // Zimmet sıralama fonksiyonu
  const handleZimmetSort = (key) => {
    setZimmetSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getZimmetSortIcon = (key) => {
    if (zimmetSortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    return zimmetSortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" /> 
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  // Filtrelenmiş ve sıralanmış zimmetler
  const filteredZimmetler = useMemo(() => {
    let result = zimmetler;
    
    // Arama filtresi
    if (zimmetSearchTerm) {
      const term = zimmetSearchTerm.toLowerCase();
      result = result.filter(z => 
        (z.urun_adi || '').toLowerCase().includes(term) ||
        (z.marka || '').toLowerCase().includes(term) ||
        (z.model || '').toLowerCase().includes(term) ||
        (z.seri_no || '').toLowerCase().includes(term) ||
        (z.kullanici_adi || '').toLowerCase().includes(term)
      );
    }
    
    // Sıralama
    if (zimmetSortConfig.key) {
      result = [...result].sort((a, b) => {
        let aVal = a[zimmetSortConfig.key] || '';
        let bVal = b[zimmetSortConfig.key] || '';
        
        if (zimmetSortConfig.key === 'created_at' || zimmetSortConfig.key === 'zimmet_tarihi') {
          aVal = new Date(aVal || 0).getTime();
          bVal = new Date(bVal || 0).getTime();
        } else {
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }
        
        if (aVal < bVal) return zimmetSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return zimmetSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [zimmetler, zimmetSearchTerm, zimmetSortConfig]);

  // Durum badge fonksiyonu
  const getDurumBadge = (durum) => {
    switch (durum) {
      case 'depoda':
        return <Badge className="bg-gray-100 text-gray-700 border-gray-300">Depoda</Badge>;
      case 'kullanicida':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-300"><UserCheck className="h-3 w-3 mr-1" />Kullanıcıda</Badge>;
      case 'bozuk':
        return <Badge className="bg-red-100 text-red-700 border-red-300"><AlertTriangle className="h-3 w-3 mr-1" />Bozuk</Badge>;
      case 'tamirde':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300"><Wrench className="h-3 w-3 mr-1" />Tamirde</Badge>;
      case 'kayip':
        return <Badge className="bg-purple-100 text-purple-700 border-purple-300"><HelpCircle className="h-3 w-3 mr-1" />Kayıp</Badge>;
      default:
        return <Badge>{durum || 'Bilinmiyor'}</Badge>;
    }
  };

  // Zimmetler Excel Export
  const exportZimmetlerToExcel = () => {
    if (filteredZimmetler.length === 0) {
      toast.error('İndirilecek veri bulunmuyor');
      return;
    }

    const rows = filteredZimmetler.map(zimmet => ({
      tarih: new Date(zimmet.created_at).toLocaleDateString('tr-TR'),
      saat: new Date(zimmet.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
      urunAdi: zimmet.urun_adi || '-',
      marka: zimmet.marka || '-',
      model: zimmet.model || '-',
      seriNo: zimmet.seri_no || '-',
      durum: zimmet.durum || 'Depoda',
      zimmetliKisi: zimmet.kullanici_adi || '-',
      aciklama: (zimmet.aciklama || '-').replace(/;/g, ',').replace(/\n/g, ' ')
    }));

    const BOM = '\uFEFF';
    const headers = ['Tarih', 'Saat', 'Ürün Adı', 'Marka', 'Model', 'Seri No', 'Durum', 'Zimmetli Kişi', 'Açıklama'];
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => [
        row.tarih, row.saat, row.urunAdi, row.marka, row.model, row.seriNo, row.durum, row.zimmetliKisi, row.aciklama
      ].join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `Zimmetler_${selectedBranch?.name || 'Tum'}_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Excel dosyası indirildi');
  };

  // Zimmet geçmişi göster
  const handleShowZimmetGecmis = async (zimmet) => {
    setSelectedZimmetGecmis(zimmet);
    try {
      const token = localStorage.getItem('fortis_token');
      const res = await fetch(`${API_URL}/api/depot/zimmetler/${zimmet.id}/gecmis`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setZimmetGecmisi(data);
        setShowGecmisModal(true);
      }
    } catch (err) {
      toast.error('Geçmiş yüklenemedi');
    }
  };

  // Taşeron seçimi ve malzemeleri yükleme
  const handleTaseronSelect = async (taseron) => {
    setSelectedTaseron(taseron);
    setTaseronMalzemeLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const res = await fetch(`${API_URL}/api/depot/taseronlar/${taseron.id}/malzemeler`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setTaseronMalzemeleri(await res.json());
      }
    } catch (err) {
      toast.error('Malzemeler yüklenemedi');
    } finally {
      setTaseronMalzemeLoading(false);
    }
  };

  // Teslim alan seçimi
  const handleTeslimAlanSelect = (kisi) => {
    setSelectedTeslimAlan(selectedTeslimAlan?.ad === kisi.ad ? null : kisi);
  };

  // Transfer fonksiyonları
  const fetchTransfers = async () => {
    setTransfersLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/depot/transfers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setTransfers(await response.json());
      }
    } catch (err) {
      console.error('Transferler yüklenemedi:', err);
    } finally {
      setTransfersLoading(false);
    }
  };

  const fetchSourceStock = async (branchId) => {
    if (!branchId) {
      setSourceStock([]);
      return;
    }
    setSourceStockLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/depot/branch-stock-for-transfer/${branchId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSourceStock(await response.json());
      }
    } catch (err) {
      console.error('Kaynak stok yüklenemedi:', err);
    } finally {
      setSourceStockLoading(false);
    }
  };

  const handleSourceBranchChange = (branchId) => {
    setTransferForm(prev => ({ ...prev, source_branch_id: branchId }));
    setSelectedTransferItems([]);
    fetchSourceStock(branchId);
  };

  const handleAddTransferItem = (stockItem) => {
    if (selectedTransferItems.find(i => i.stok_karti_adi === stockItem.stok_karti_adi)) {
      toast.error('Bu ürün zaten eklendi');
      return;
    }
    setSelectedTransferItems(prev => [...prev, {
      product_name: stockItem.stok_karti_adi,
      unit: stockItem.birim,
      available_quantity: stockItem.miktar,
      quantity: 1
    }]);
  };

  const handleTransferItemQuantityChange = (index, quantity) => {
    setSelectedTransferItems(prev => {
      const newItems = [...prev];
      const maxQty = newItems[index].available_quantity;
      newItems[index].quantity = Math.min(Math.max(1, parseFloat(quantity) || 1), maxQty);
      return newItems;
    });
  };

  const handleRemoveTransferItem = (index) => {
    setSelectedTransferItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitTransfer = async () => {
    if (!transferForm.source_branch_id || !transferForm.destination_branch_id) {
      toast.error('Kaynak ve hedef şube seçmelisiniz');
      return;
    }
    if (selectedTransferItems.length === 0) {
      toast.error('En az bir ürün eklemelisiniz');
      return;
    }

    setTransferSubmitting(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Eğer bir iş'den gelindiyse, taslak olarak kaydet
      if (transferForm.related_job_id) {
        // Dosyaları base64'e çevir (opsiyonel)
        const convertToBase64 = (file) => {
          if (!file) return Promise.resolve(null);
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve({ name: file.name, data: reader.result });
            reader.onerror = (error) => reject(error);
          });
        };
        
        const teslimAlanBase64 = await convertToBase64(transferForm.teslim_alan_belgesi);
        const teslimEdenBase64 = await convertToBase64(transferForm.teslim_eden_belgesi);
        
        const draftData = {
          source_branch_id: transferForm.source_branch_id,
          destination_branch_id: transferForm.destination_branch_id,
          items: selectedTransferItems.map(item => ({
            product_name: item.product_name,
            quantity: item.quantity,
            unit: item.unit
          })),
          description: transferForm.description || ''
        };
        
        // Belgeler varsa ekle
        if (teslimAlanBase64) {
          draftData.teslim_alan_belgesi = teslimAlanBase64;
        }
        if (teslimEdenBase64) {
          draftData.teslim_eden_belgesi = teslimEdenBase64;
        }
        
        const draftResponse = await fetch(`${API_URL}/api/purchasing/jobs/${transferForm.related_job_id}/save-draft`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            draft_type: 'transfer',
            draft_data: draftData
          })
        });
        
        if (draftResponse.ok) {
          toast.success('Transfer taslağı kaydedildi, yönetim onayı bekleniyor');
          setShowTransferModal(false);
          setTransferForm({ 
            source_branch_id: '', 
            destination_branch_id: '', 
            items: [], 
            description: '',
            teslim_alan_belgesi: null,
            teslim_eden_belgesi: null,
            related_job_id: null
          });
          setSelectedTransferItems([]);
          // İşler sayfasına yönlendir
          window.location.href = '/purchasing/jobs?tab=completed';
        } else {
          const err = await draftResponse.json();
          toast.error(err.detail || 'Taslak kaydetme başarısız');
        }
        return;
      }
      
      // Normal akış - direkt transfer yap
      const formData = new FormData();
      formData.append('source_branch_id', transferForm.source_branch_id);
      formData.append('destination_branch_id', transferForm.destination_branch_id);
      formData.append('items', JSON.stringify(selectedTransferItems.map(item => ({
        product_name: item.product_name,
        quantity: item.quantity,
        unit: item.unit
      }))));
      formData.append('description', transferForm.description || '');
      // İlişkili iş ID'si (varsa)
      if (transferForm.related_job_id) {
        formData.append('source_job_id', transferForm.related_job_id);
      }
      // Belgeler artık opsiyonel
      if (transferForm.teslim_alan_belgesi) {
        formData.append('teslim_alan_belgesi', transferForm.teslim_alan_belgesi);
      }
      if (transferForm.teslim_eden_belgesi) {
        formData.append('teslim_eden_belgesi', transferForm.teslim_eden_belgesi);
      }

      const response = await fetch(`${API_URL}/api/depot/transfers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        toast.success('Transfer başarıyla tamamlandı');
        
        setShowTransferModal(false);
        setTransferForm({ 
          source_branch_id: '', 
          destination_branch_id: '', 
          items: [], 
          description: '',
          teslim_alan_belgesi: null,
          teslim_eden_belgesi: null
        });
        setSelectedTransferItems([]);
        setSourceStock([]);
        fetchTransfers();
        // Depo verilerini yenile
        if (selectedBranch) {
          fetchDepotData(selectedBranch.id);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Transfer başarısız');
      }
    } catch (err) {
      toast.error('Transfer hatası');
    } finally {
      setTransferSubmitting(false);
    }
  };

  const openTransferModal = () => {
    setShowTransferModal(true);
    setTransferForm({ 
      source_branch_id: '', 
      destination_branch_id: '', 
      items: [], 
      description: '',
      teslim_alan_belgesi: null,
      teslim_eden_belgesi: null
    });
    setSelectedTransferItems([]);
    setSourceStock([]);
  };

  const openTransferHistory = () => {
    fetchTransfers();
    setShowTransferHistoryModal(true);
  };

  // URL parametrelerinden transfer modalını açma
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenTransferModal = searchParams.get('openTransferModal') === 'true';
    const urlJobId = searchParams.get('jobId');
    const urlJobName = searchParams.get('jobName');
    
    // localStorage'dan backup değerleri al
    const storedJobId = localStorage.getItem('draft_source_job_id');
    const storedJobName = localStorage.getItem('draft_source_job_name');
    
    // URL veya localStorage'dan final değerleri belirle
    const finalJobId = urlJobId || storedJobId;
    const finalJobName = urlJobName || storedJobName;
    
    if ((shouldOpenTransferModal || storedJobId) && !loading) {
      // localStorage'ı temizle
      localStorage.removeItem('draft_source_job_id');
      localStorage.removeItem('draft_source_job_name');
      
      setShowTransferModal(true);
      setTransferForm({ 
        source_branch_id: '', 
        destination_branch_id: '', 
        items: [], 
        description: finalJobName ? `${decodeURIComponent(finalJobName)} işi için transfer` : '',
        teslim_alan_belgesi: null,
        teslim_eden_belgesi: null,
        related_job_id: finalJobId || '',
        related_job_name: finalJobName ? decodeURIComponent(finalJobName) : ''
      });
      setSelectedTransferItems([]);
      setSourceStock([]);
      
      // URL'i temizle
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, loading]);

  // Sub-tab counts
  const openDefectiveCount = hataliUrunler.filter(u => u.status === 'acik').length;

  // Sub-tab configuration
  const subTabs = [
    { id: 'giris', label: 'Depo Giriş', icon: PackagePlus, count: stokGirisleri.length },
    { id: 'cikis', label: 'Stok Çıkış', icon: PackageMinus, count: stokCikislari.length },
    { id: 'stok', label: 'Depo Stok', icon: Boxes, count: depoStok.length },
    { id: 'zimmetler', label: 'Zimmetler', icon: UserCheck, count: zimmetler.length },
    { id: 'taseronlar', label: 'Taşeronlar', icon: Truck, count: taseronlar.length },
    { id: 'teslim-alanlar', label: 'Teslim Alanlar', icon: User, count: teslimAlanlar.length }
  ];

  // Render loading state
  if (branchesLoading) {
    return (
      <div className="space-y-6" data-testid="purchasing-warehouses-loading">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Depolar</h1>
          <p className="text-gray-500">Şube depolarının durumunu görüntüleyin</p>
        </div>
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchasing-warehouses">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Depolar</h1>
          <p className="text-gray-500">Şube depolarının durumunu görüntüleyin</p>
        </div>
      </div>

      {/* Main Tabs - Stok + Branch Tabs */}
      {branches.length > 0 ? (
        <Tabs 
          value={activeMainTab} 
          onValueChange={(value) => {
            setActiveMainTab(value);
            if (value === 'stok-genel' || value === 'cari-hesaplar' || value === 'stok-kartlari') {
              setSelectedBranch(null);
            } else {
              const branch = branches.find(b => b.id === value);
              setSelectedBranch(branch);
            }
            setActiveSubTab('giris');
            clearFilters();
          }}
          className="space-y-4"
        >
          <TabsList className="flex flex-wrap gap-2 h-auto p-2 bg-orange-50">
            {/* Stok Tab - First */}
            <TabsTrigger 
              value="stok-genel"
              className="flex items-center gap-2 data-[state=active]:bg-orange-600 data-[state=active]:text-white"
              data-testid="tab-stok-genel"
            >
              <Layers className="h-4 w-4" />
              Stok
            </TabsTrigger>

            {/* Branch Tabs */}
            {branches.map((branch) => (
              <TabsTrigger 
                key={branch.id}
                value={branch.id}
                className="flex items-center gap-2 data-[state=active]:bg-orange-600 data-[state=active]:text-white"
                data-testid={`branch-tab-${branch.id}`}
              >
                <Building className="h-4 w-4" />
                {branch.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Stok Genel Content */}
          <TabsContent value="stok-genel" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="h-5 w-5 text-orange-600" />
                    Genel Stok Özeti
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openTransferHistory}
                      className="gap-2 text-blue-700 border-blue-300 hover:bg-blue-50"
                    >
                      <History className="h-4 w-4" />
                      Transfer Geçmişi
                    </Button>
                    <Button
                      onClick={openTransferModal}
                      className="gap-2 bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      <ArrowLeftRight className="h-4 w-4" />
                      Depolar Arası Transfer
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <PurchasingStock />
              </CardContent>
            </Card>
          </TabsContent>

          {/* REMOVED: Cari Hesaplar and Stok Kartları tabs - use Cari Hesap & Stok Kartı page instead */}

          {/* Branch Contents */}
          {branches.map((branch) => (
            <TabsContent key={branch.id} value={branch.id} className="space-y-4">
              {/* Sub-tabs for depot sections */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2">
                    <Warehouse className="h-5 w-5 text-orange-600" />
                    {branch.name} - Depo Yönetimi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="space-y-4">
                    <TabsList className="grid w-full grid-cols-6">
                      {subTabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                          <TabsTrigger 
                            key={tab.id}
                            value={tab.id}
                            className="gap-2"
                            data-testid={`subtab-${tab.id}`}
                          >
                            <Icon className="h-4 w-4" />
                            <span className="hidden md:inline">{tab.label}</span>
                            {tab.count > 0 && (
                              <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1 text-xs">
                                {tab.count}
                              </Badge>
                            )}
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>

                    {/* DEPO GİRİŞ TAB */}
                    <TabsContent value="giris">
                      {loading ? (
                        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
                      ) : (
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-lg">
                                <PackagePlus className="h-5 w-5 text-emerald-600" />
                                Stok Girişleri ({stokGirisleri.length})
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const productMap = {};
                                    stokGirisleri.forEach(giris => {
                                      giris.items?.forEach(item => {
                                        const productName = item.product_name;
                                        const stokKarti = stokKartlari.find(k => k.product_name === productName);
                                        if (!productMap[productName]) {
                                          productMap[productName] = {
                                            product_name: productName,
                                            unit: item.unit,
                                            total_quantity: 0,
                                            max_usage: stokKarti?.max_usage || 0,
                                            entry_count: 0,
                                            last_entry_date: giris.created_at
                                          };
                                        }
                                        productMap[productName].total_quantity += parseFloat(item.quantity) || 0;
                                        productMap[productName].entry_count += 1;
                                        if (new Date(giris.created_at) > new Date(productMap[productName].last_entry_date)) {
                                          productMap[productName].last_entry_date = giris.created_at;
                                        }
                                      });
                                    });
                                    exportToExcel(Object.values(productMap), 'Depo_Giris', [
                                      { key: 'product_name', label: 'Ürün Adı' },
                                      { key: 'total_quantity', label: 'Toplam Giriş', type: 'number' },
                                      { key: 'max_usage', label: 'Max Kullanım', type: 'number' },
                                      { key: 'unit', label: 'Birim' },
                                      { key: 'entry_count', label: 'Giriş Sayısı', type: 'number' },
                                      { key: 'last_entry_date', label: 'Son Giriş' }
                                    ]);
                                  }}
                                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Excel İndir
                                </Button>
                                <Button 
                                  onClick={openStokEkleModal}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  data-testid="stok-ekle-btn"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Stok Ekle
                                </Button>
                              </div>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {stokGirisleri.length === 0 ? (
                              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <PackagePlus className="h-12 w-12 mb-4 text-gray-300" />
                                <p>Henüz stok girişi bulunmuyor</p>
                              </div>
                            ) : (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <SortableHeader sortKey="product_name" currentSort={girisSort} onSort={(key) => handleSort(setGirisSort, girisSort, key)}>
                                      Ürün Adı
                                    </SortableHeader>
                                    <SortableHeader sortKey="total_quantity" currentSort={girisSort} onSort={(key) => handleSort(setGirisSort, girisSort, key)}>
                                      <span className="text-right w-full">Toplam Giriş</span>
                                    </SortableHeader>
                                    <SortableHeader sortKey="max_usage" currentSort={girisSort} onSort={(key) => handleSort(setGirisSort, girisSort, key)}>
                                      <span className="text-right w-full">Max Kullanım</span>
                                    </SortableHeader>
                                    <TableHead>Birim</TableHead>
                                    <SortableHeader sortKey="entry_count" currentSort={girisSort} onSort={(key) => handleSort(setGirisSort, girisSort, key)}>
                                      <span className="text-center w-full">Giriş Sayısı</span>
                                    </SortableHeader>
                                    <SortableHeader sortKey="last_entry_date" currentSort={girisSort} onSort={(key) => handleSort(setGirisSort, girisSort, key)}>
                                      Son Giriş
                                    </SortableHeader>
                                    <TableHead className="text-right">İşlemler</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(() => {
                                    const productMap = {};
                                    stokGirisleri.forEach(giris => {
                                      giris.items?.forEach(item => {
                                        const productName = item.product_name;
                                        const stokKarti = stokKartlari.find(k => k.product_name === productName);
                                        if (!productMap[productName]) {
                                          productMap[productName] = {
                                            product_name: productName,
                                            unit: item.unit,
                                            total_quantity: 0,
                                            max_usage: stokKarti?.max_usage || 0,
                                            entry_count: 0,
                                            last_entry_date: giris.created_at,
                                            entries: []
                                          };
                                        }
                                        productMap[productName].total_quantity += parseFloat(item.quantity) || 0;
                                        productMap[productName].entry_count += 1;
                                        if (new Date(giris.created_at) > new Date(productMap[productName].last_entry_date)) {
                                          productMap[productName].last_entry_date = giris.created_at;
                                        }
                                        productMap[productName].entries.push({
                                          date: giris.created_at,
                                          quantity: item.quantity,
                                          unit: item.unit,
                                          cari_hesap: giris.cari_hesap_name,
                                          description: giris.description,
                                          documents: giris.documents
                                        });
                                      });
                                    });

                                    const productList = sortData(Object.values(productMap), girisSort);

                                    return productList.map((product, idx) => (
                                      <TableRow key={idx} className="cursor-pointer hover:bg-gray-50" onClick={() => {
                                        setSelectedProductHistory(product);
                                        setProductHistoryModal(true);
                                      }}>
                                        <TableCell className="font-medium">
                                          <div className="flex items-center gap-2">
                                            <Package className="h-4 w-4 text-emerald-500" />
                                            {product.product_name}
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-lg text-emerald-600">
                                          {product.total_quantity}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-blue-600">
                                          {product.max_usage || '-'}
                                        </TableCell>
                                        <TableCell>{product.unit}</TableCell>
                                        <TableCell className="text-center">
                                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                            {product.entry_count} giriş
                                          </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm text-gray-500">
                                          {formatDate(product.last_entry_date)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedProductHistory(product);
                                              setProductHistoryModal(true);
                                            }}
                                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                          >
                                            <Eye className="h-4 w-4" />
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    ));
                                  })()}
                                </TableBody>
                              </Table>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* STOK ÇIKIŞ TAB */}
                    <TabsContent value="cikis">
                      {loading ? (
                        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
                      ) : (
                        <Card>
                          <CardHeader className="flex flex-col gap-4">
                            <div className="flex flex-row items-center justify-between">
                              <CardTitle className="flex items-center gap-2 text-lg">
                                <PackageMinus className="h-5 w-5 text-emerald-600" />
                                Stok Çıkışları ({filteredCikislar.length}/{stokCikislari.length})
                              </CardTitle>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const exportData = filteredCikislar.map(cikis => ({
                                      created_at: cikis.created_at,
                                      requester_name: cikis.requester_name || '-',
                                      products: cikis.items?.map(i => i.product_name).join(', ') || '-',
                                      quantities: cikis.items?.map(i => `${i.quantity} ${i.unit}`).join(', ') || '-',
                                      taseron_firma: cikis.taseron_firma || '-',
                                      description: cikis.description || '-',
                                      created_by_name: cikis.created_by_name || '-'
                                    }));
                                    exportToExcel(exportData, 'Stok_Cikis', [
                                      { key: 'created_at', label: 'Tarih' },
                                      { key: 'requester_name', label: 'Talep Eden' },
                                      { key: 'products', label: 'Ürünler' },
                                      { key: 'quantities', label: 'Miktar' },
                                      { key: 'taseron_firma', label: 'Taşeron Firma' },
                                      { key: 'description', label: 'Açıklama' },
                                      { key: 'created_by_name', label: 'İşlemi Yapan' }
                                    ]);
                                  }}
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Excel İndir
                                </Button>
                                <Button
                                  variant={showFilters ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setShowFilters(!showFilters)}
                                  className={showFilters ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                                >
                                  <Filter className="h-4 w-4 mr-2" />
                                  Filtrele
                                </Button>
                              </div>
                            </div>
                            {showFilters && (
                              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border">
                                <div className="space-y-1">
                                  <Label className="text-xs text-gray-500">Başlangıç Tarihi</Label>
                                  <Input
                                    type="date"
                                    value={filters.dateFrom}
                                    onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-gray-500">Bitiş Tarihi</Label>
                                  <Input
                                    type="date"
                                    value={filters.dateTo}
                                    onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-gray-500">Ürün</Label>
                                  <Select value={filters.product} onValueChange={(val) => setFilters(prev => ({ ...prev, product: val === 'all' ? '' : val }))}>
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Tümü" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="all">Tümü</SelectItem>
                                      {uniqueProducts.map(p => (
                                        <SelectItem key={p} value={p}>{p}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            )}
                          </CardHeader>
                          <CardContent>
                            {filteredCikislar.length === 0 ? (
                              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <PackageMinus className="h-12 w-12 mb-4 text-gray-300" />
                                <p>{hasActiveFilters ? 'Filtrelere uygun kayıt bulunamadı' : 'Henüz stok çıkışı bulunmuyor'}</p>
                              </div>
                            ) : (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <SortableHeader sortKey="created_at" currentSort={cikisSort} onSort={(key) => handleSort(setCikisSort, cikisSort, key)}>
                                      Tarih
                                    </SortableHeader>
                                    <SortableHeader sortKey="requester_name" currentSort={cikisSort} onSort={(key) => handleSort(setCikisSort, cikisSort, key)}>
                                      Talep Eden
                                    </SortableHeader>
                                    <TableHead>Ürünler</TableHead>
                                    <TableHead>Miktar</TableHead>
                                    <SortableHeader sortKey="taseron_firma" currentSort={cikisSort} onSort={(key) => handleSort(setCikisSort, cikisSort, key)}>
                                      Taşeron Firma
                                    </SortableHeader>
                                    <TableHead>Teslim Belgeleri</TableHead>
                                    <TableHead>Açıklama</TableHead>
                                    <SortableHeader sortKey="created_by_name" currentSort={cikisSort} onSort={(key) => handleSort(setCikisSort, cikisSort, key)}>
                                      İşlemi Yapan
                                    </SortableHeader>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {sortData(filteredCikislar, cikisSort).map((cikis) => (
                                    <TableRow key={cikis.id}>
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          <Clock className="h-4 w-4 text-gray-400" />
                                          {formatDate(cikis.created_at)}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          <User className="h-4 w-4 text-gray-400" />
                                          {cikis.requester_name || '-'}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="space-y-1">
                                          {cikis.items?.map((item, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-sm">
                                              <Package className="h-3 w-3 text-emerald-500" />
                                              <span className="font-medium">{item.product_name}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="space-y-1">
                                          {cikis.items?.map((item, idx) => (
                                            <div key={idx} className="text-sm font-medium text-emerald-600">
                                              {item.quantity} {item.unit}
                                            </div>
                                          ))}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        {cikis.taseron_firma ? (
                                          <div className="flex items-center gap-2">
                                            <Building2 className="h-4 w-4 text-blue-500" />
                                            <span className="text-sm font-medium text-blue-600">{cikis.taseron_firma}</span>
                                          </div>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell>
                                        {cikis.teslim_belgeleri?.length > 0 ? (
                                          <div className="space-y-1">
                                            {cikis.teslim_belgeleri.map((doc, idx) => (
                                              <div key={idx} className="flex items-center gap-1 bg-green-50 border border-green-200 rounded px-2 py-1">
                                                <FileText className="h-3 w-3 text-green-600" />
                                                <span className="text-xs text-green-700 truncate max-w-[80px]">{doc.name}</span>
                                                {doc.data && (
                                                  <a href={doc.data} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                                                    <Eye className="h-3 w-3" />
                                                  </a>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell className="max-w-xs truncate">{cikis.description || '-'}</TableCell>
                                      <TableCell className="text-sm text-gray-500">{cikis.created_by_name || '-'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* DEPO STOK TAB */}
                    <TabsContent value="stok">
                      {loading ? (
                        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
                      ) : (
                        <>
                          {/* Summary Cards */}
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <Card>
                              <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                  <div className="p-3 rounded-full bg-emerald-100">
                                    <Boxes className="h-6 w-6 text-emerald-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Toplam Ürün Çeşidi</p>
                                    <p className="text-2xl font-bold">{totalItems}</p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                  <div className="p-3 rounded-full bg-amber-100">
                                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Kritik Seviyede</p>
                                    <p className="text-2xl font-bold text-amber-600">{lowStockItems}</p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                  <div className="p-3 rounded-full bg-red-100">
                                    <Package className="h-6 w-6 text-red-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Tükenen Stok</p>
                                    <p className="text-2xl font-bold text-red-600">{outOfStockItems}</p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </div>

                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-lg">
                                  <Boxes className="h-5 w-5 text-emerald-600" />
                                  Stok Listesi
                                </div>
                                <div className="flex items-center gap-2">
                                  {selectedItems.length > 0 && (
                                    <>
                                      <span className="text-sm text-gray-600">{selectedItems.length} seçildi</span>
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => handleBulkDeleteStock(selectedBranch?.id)}
                                        disabled={bulkDeleteLoading}
                                      >
                                        {bulkDeleteLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                                        Seçilenleri Sil
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setSelectedItems([])}
                                      >
                                        Temizle
                                      </Button>
                                    </>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      exportToExcel(filteredStok, 'Depo_Stok', [
                                        { key: 'product_name', label: 'Ürün Adı' },
                                        { key: 'quantity', label: 'Mevcut', type: 'number' },
                                        { key: 'total_giris', label: 'Toplam Giriş', type: 'number' },
                                        { key: 'total_cikis', label: 'Toplam Çıkış', type: 'number' },
                                        { key: 'minimum_stock', label: 'Min Stok', type: 'number' },
                                        { key: 'maximum_stock', label: 'Max Stok', type: 'number' },
                                        { key: 'unit', label: 'Birim' },
                                        { key: 'updated_at', label: 'Son Güncelleme' }
                                      ]);
                                    }}
                                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Excel İndir
                                  </Button>
                                </div>
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="mb-4">
                                <div className="relative">
                                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                  <Input
                                    placeholder="Ürün ara..."
                                    value={stokSearch}
                                    onChange={(e) => setStokSearch(e.target.value)}
                                    className="pl-10"
                                  />
                                </div>
                              </div>
                              {filteredStok.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                  <Boxes className="h-12 w-12 mb-4 text-gray-300" />
                                  <p>Stok bulunamadı</p>
                                </div>
                              ) : (
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="w-12">
                                        <Checkbox 
                                          checked={filteredStok.length > 0 && sortData(filteredStok, depoStokSort).every(o => selectedItems.includes(o.id))}
                                          onCheckedChange={() => toggleSelectAll(sortData(filteredStok, depoStokSort))}
                                        />
                                      </TableHead>
                                      <SortableHeader sortKey="product_name" currentSort={depoStokSort} onSort={(key) => handleSort(setDepoStokSort, depoStokSort, key)}>
                                        Ürün Adı
                                      </SortableHeader>
                                      <SortableHeader sortKey="quantity" currentSort={depoStokSort} onSort={(key) => handleSort(setDepoStokSort, depoStokSort, key)}>
                                        <span className="text-right w-full">Mevcut</span>
                                      </SortableHeader>
                                      <SortableHeader sortKey="total_giris" currentSort={depoStokSort} onSort={(key) => handleSort(setDepoStokSort, depoStokSort, key)}>
                                        <span className="text-right w-full">Toplam Giriş</span>
                                      </SortableHeader>
                                      <SortableHeader sortKey="total_cikis" currentSort={depoStokSort} onSort={(key) => handleSort(setDepoStokSort, depoStokSort, key)}>
                                        <span className="text-right w-full">Toplam Çıkış</span>
                                      </SortableHeader>
                                      <SortableHeader sortKey="minimum_stock" currentSort={depoStokSort} onSort={(key) => handleSort(setDepoStokSort, depoStokSort, key)}>
                                        <span className="text-right w-full">Min / Max Stok</span>
                                      </SortableHeader>
                                      <TableHead>Birim</TableHead>
                                      <TableHead>Durum</TableHead>
                                      <SortableHeader sortKey="updated_at" currentSort={depoStokSort} onSort={(key) => handleSort(setDepoStokSort, depoStokSort, key)}>
                                        Son Güncelleme
                                      </SortableHeader>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {sortData(filteredStok, depoStokSort).map((item) => {
                                      const status = getStockStatus(item.quantity, item.minimum_stock);
                                      return (
                                        <TableRow key={item.id} className={selectedItems.includes(item.id) ? 'bg-red-50' : ''}>
                                          <TableCell className="w-12">
                                            <Checkbox 
                                              checked={selectedItems.includes(item.id)}
                                              onCheckedChange={() => toggleItemSelection(item.id)}
                                            />
                                          </TableCell>
                                          <TableCell className="font-medium">
                                            <div className="flex items-center gap-2">
                                              <Package className="h-4 w-4 text-emerald-500" />
                                              {item.product_name}
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-right font-mono text-lg">{item.quantity}</TableCell>
                                          <TableCell className="text-right font-mono text-emerald-600">+{item.total_giris || 0}</TableCell>
                                          <TableCell className="text-right font-mono text-red-500">-{item.total_cikis || 0}</TableCell>
                                          <TableCell className="text-right">
                                            <div className="flex flex-col items-end">
                                              <span className="font-mono text-amber-600">{item.minimum_stock || 0}</span>
                                              <span className="text-xs text-gray-400">/</span>
                                              <span className="font-mono text-blue-600">{item.maximum_stock || '-'}</span>
                                            </div>
                                          </TableCell>
                                          <TableCell>{item.unit}</TableCell>
                                          <TableCell>
                                            <Badge className={status.className}>{status.label}</Badge>
                                          </TableCell>
                                          <TableCell className="text-sm text-gray-500">
                                            {item.updated_at ? formatDate(item.updated_at) : '-'}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                            </CardContent>
                          </Card>
                        </>
                      )}
                    </TabsContent>

                    {/* GELECEK ÜRÜNLER TAB */}
                    <TabsContent value="gelecek">
                      {loading ? (
                        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
                      ) : (
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                              <Truck className="h-5 w-5 text-emerald-600" />
                              Gelecek Ürünler ({gelecekUrunler.length})
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {gelecekUrunler.length === 0 ? (
                              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <Truck className="h-12 w-12 mb-4 text-gray-300" />
                                <p>Henüz kayıt bulunmuyor</p>
                              </div>
                            ) : (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Tedarikçi</TableHead>
                                    <TableHead>Ürün</TableHead>
                                    <TableHead>Miktar</TableHead>
                                    <TableHead>Beklenen Tarih</TableHead>
                                    <TableHead>Durum</TableHead>
                                    <TableHead>Notlar</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {gelecekUrunler.map((urun) => (
                                    <TableRow key={urun.id}>
                                      <TableCell className="font-medium">{urun.cari_hesap_name || '-'}</TableCell>
                                      <TableCell>{urun.product_name}</TableCell>
                                      <TableCell>{urun.quantity} {urun.unit}</TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          <Calendar className="h-4 w-4 text-gray-400" />
                                          {urun.expected_date ? formatDate(urun.expected_date) : '-'}
                                        </div>
                                      </TableCell>
                                      <TableCell>{getStatusBadge(urun.status)}</TableCell>
                                      <TableCell className="max-w-xs truncate">{urun.notes || '-'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* HATALI/EKSİK ÜRÜNLER TAB */}
                    <TabsContent value="hatali">
                      {loading ? (
                        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
                      ) : (
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                              <AlertTriangle className="h-5 w-5 text-red-600" />
                              Hatalı veya Eksik Ürünler ({hataliUrunler.length})
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {hataliUrunler.length === 0 ? (
                              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <AlertTriangle className="h-12 w-12 mb-4 text-gray-300" />
                                <p>Sorunlu ürün kaydı bulunmuyor</p>
                              </div>
                            ) : (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Tarih</TableHead>
                                    <TableHead>Ürün</TableHead>
                                    <TableHead>Hata Durumu</TableHead>
                                    <TableHead>Miktar</TableHead>
                                    <TableHead>Cari Hesap</TableHead>
                                    <TableHead>Hata Çıkma Zamanı</TableHead>
                                    <TableHead>Belgeler</TableHead>
                                    <TableHead>Durum</TableHead>
                                    <TableHead className="text-right">İşlemler</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {hataliUrunler.map((urun) => (
                                    <TableRow key={urun.id}>
                                      <TableCell className="text-sm text-gray-500">{formatDate(urun.created_at)}</TableCell>
                                      <TableCell className="font-medium">{urun.product_name}</TableCell>
                                      <TableCell>{getIssueTypeBadge(urun.issue_type)}</TableCell>
                                      <TableCell>{urun.quantity} {urun.unit}</TableCell>
                                      <TableCell>{urun.cari_hesap || '-'}</TableCell>
                                      <TableCell className="text-sm">{getHataCikmaZamaniLabel(urun.hata_cikma_zamani)}</TableCell>
                                      <TableCell>
                                        {urun.belgeler && urun.belgeler.length > 0 ? (
                                          <Badge variant="outline" className="cursor-pointer" onClick={() => openDetailModal(urun)}>
                                            <FileText className="h-3 w-3 mr-1" />
                                            {urun.belgeler.length} belge
                                          </Badge>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell>{getDefectiveStatusBadge(urun.status)}</TableCell>
                                      <TableCell className="text-right">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => openDetailModal(urun)}
                                          className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* ZİMMETLER TAB */}
                    {/* ZİMMETLER TAB - Depo Paneli Yapısı */}
                    <TabsContent value="zimmetler">
                      <Card>
                        <CardHeader className="flex flex-col gap-4">
                          <div className="flex flex-row items-center justify-between">
                            <CardTitle className="flex items-center gap-2">
                              <UserCheck className="h-5 w-5 text-orange-600" />
                              Zimmetler ({filteredZimmetler.length})
                            </CardTitle>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={exportZimmetlerToExcel}
                                className="gap-1 text-orange-700 border-orange-300 hover:bg-orange-50"
                              >
                                <Download className="h-4 w-4" />
                                Excel İndir
                              </Button>
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                  placeholder="Ara... (ürün, marka, seri no)"
                                  value={zimmetSearchTerm}
                                  onChange={(e) => setZimmetSearchTerm(e.target.value)}
                                  className="pl-9 w-64 h-9"
                                />
                              </div>
                            </div>
                          </div>
                          
                          {showZimmetInfo && (
                            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg relative">
                              <button 
                                onClick={() => {
                                  setShowZimmetInfo(false);
                                  localStorage.setItem('purchasing_zimmet_info_hidden', 'true');
                                }}
                                className="absolute top-2 right-2 p-1 hover:bg-orange-100 rounded-full transition-colors"
                              >
                                <X className="h-4 w-4 text-orange-600" />
                              </button>
                              <div className="flex gap-3 pr-6">
                                <Info className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-orange-800">
                                  Zimmetler bölümünde bu şubenin stoğuna girmiş demirbaşların hareketlerini buradan takip edebilirsiniz. 
                                  Bir zimmetin geçmişini görmek için sağdaki <History className="h-3 w-3 inline" /> butonuna tıklayın.
                                </p>
                              </div>
                            </div>
                          )}
                        </CardHeader>
                        <CardContent>
                          {loading ? (
                            <div className="space-y-3">
                              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                            </div>
                          ) : filteredZimmetler.length === 0 ? (
                            <div className="text-center py-10 text-gray-500">
                              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                              <p>{zimmetSearchTerm ? 'Aramanıza uygun zimmet bulunamadı' : 'Bu şubeye ait zimmet kaydı bulunmuyor'}</p>
                            </div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleZimmetSort('created_at')}
                                  >
                                    <div className="flex items-center">Tarih {getZimmetSortIcon('created_at')}</div>
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleZimmetSort('urun_adi')}
                                  >
                                    <div className="flex items-center">Ürün Adı {getZimmetSortIcon('urun_adi')}</div>
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleZimmetSort('marka')}
                                  >
                                    <div className="flex items-center">Marka {getZimmetSortIcon('marka')}</div>
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleZimmetSort('model')}
                                  >
                                    <div className="flex items-center">Model {getZimmetSortIcon('model')}</div>
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleZimmetSort('seri_no')}
                                  >
                                    <div className="flex items-center">Seri No {getZimmetSortIcon('seri_no')}</div>
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleZimmetSort('durum')}
                                  >
                                    <div className="flex items-center">Durum {getZimmetSortIcon('durum')}</div>
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleZimmetSort('kullanici_adi')}
                                  >
                                    <div className="flex items-center">Kullanıcı {getZimmetSortIcon('kullanici_adi')}</div>
                                  </TableHead>
                                  <TableHead 
                                    className="cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleZimmetSort('kabul_durumu')}
                                  >
                                    <div className="flex items-center">Yanıt Durumu {getZimmetSortIcon('kabul_durumu')}</div>
                                  </TableHead>
                                  <TableHead className="text-right">İşlemler</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {filteredZimmetler.map((zimmet) => (
                                  <TableRow key={zimmet.id}>
                                    <TableCell>
                                      <div className="flex flex-col">
                                        <span>{new Date(zimmet.created_at).toLocaleDateString('tr-TR')}</span>
                                        <span className="text-xs text-gray-500">{new Date(zimmet.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                                      </div>
                                    </TableCell>
                                    <TableCell className="font-medium">{zimmet.urun_adi || '-'}</TableCell>
                                    <TableCell>{zimmet.marka || '-'}</TableCell>
                                    <TableCell>{zimmet.model || '-'}</TableCell>
                                    <TableCell className="font-mono text-sm">{zimmet.seri_no || '-'}</TableCell>
                                    <TableCell>{getDurumBadge(zimmet.durum)}</TableCell>
                                    <TableCell>
                                      {zimmet.kullanici_adi ? (
                                        <div className="flex items-center gap-1">
                                          <UserCheck className="h-4 w-4 text-blue-600" />
                                          <span className="text-blue-700 font-medium">{zimmet.kullanici_adi}</span>
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {zimmet.durum === 'kullanicida' ? (
                                        zimmet.kabul_durumu === 'accepted' ? (
                                          <Badge className="bg-green-100 text-green-700">Teslim Aldı</Badge>
                                        ) : zimmet.kabul_durumu === 'rejected' ? (
                                          <Badge className="bg-red-100 text-red-700">Reddetti</Badge>
                                        ) : (
                                          <Badge className="bg-yellow-100 text-yellow-700">Yanıt Vermedi</Badge>
                                        )
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleShowZimmetGecmis(zimmet)}
                                        className="text-gray-600 hover:text-gray-800 h-8 w-8 p-0"
                                        title="Geçmişi Gör"
                                      >
                                        <History className="h-4 w-4" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* TAŞERONLAR TAB - Depo Paneli Yapısı (İki Panelli) */}
                    <TabsContent value="taseronlar">
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-2">
                              <Building2 className="h-5 w-5 text-orange-600" />
                              Taşeronlara Verilen Malzemeler
                            </CardTitle>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Tüm taşeron malzemelerini topla
                                const allMalzemeler = [];
                                taseronlar.forEach(taseron => {
                                  if (taseron.malzemeler && taseron.malzemeler.length > 0) {
                                    taseron.malzemeler.forEach(malzeme => {
                                      allMalzemeler.push({
                                        taseron_adi: taseron.name,
                                        tarih: malzeme.teslim_tarihi || malzeme.created_at,
                                        urun_adi: malzeme.urun_adi || malzeme.product_name || '-',
                                        miktar: `${malzeme.miktar || malzeme.quantity || 0} ${malzeme.birim || malzeme.unit || 'adet'}`,
                                        teslim_alan: malzeme.teslim_alan || '-',
                                        teslim_eden: malzeme.teslim_eden || '-'
                                      });
                                    });
                                  }
                                });
                                
                                if (allMalzemeler.length === 0) {
                                  toast.error('İndirilecek veri bulunmuyor');
                                  return;
                                }
                                
                                exportToExcel(allMalzemeler, 'Taseron_Malzemeleri', [
                                  { key: 'taseron_adi', label: 'Taşeron' },
                                  { key: 'tarih', label: 'Tarih' },
                                  { key: 'urun_adi', label: 'Ürün Adı' },
                                  { key: 'miktar', label: 'Miktar' },
                                  { key: 'teslim_alan', label: 'Teslim Alan' },
                                  { key: 'teslim_eden', label: 'Teslim Eden' }
                                ]);
                              }}
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                              data-testid="taseron-excel-btn"
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Excel İndir
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {showTaseronInfo && (
                            <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg relative">
                              <button 
                                onClick={() => {
                                  setShowTaseronInfo(false);
                                  localStorage.setItem('purchasing_taseron_info_hidden', 'true');
                                }}
                                className="absolute top-2 right-2 text-orange-400 hover:text-orange-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                              <div className="flex items-start gap-3">
                                <Info className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-orange-800">
                                  <strong>Taşeron Malzemeleri:</strong> Bu bölümde şubede çalışan taşeronlara verilen malzemeleri takip edebilirsiniz. 
                                  Bir taşerona tıklayarak verilen malzemeleri görebilirsiniz.
                                </div>
                              </div>
                            </div>
                          )}

                          {loading ? (
                            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
                          ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                              {/* Sol Panel - Taşeron Listesi */}
                              <div className="lg:col-span-1">
                                <div className="border rounded-lg">
                                  <div className="p-3 bg-gray-50 border-b">
                                    <div className="relative">
                                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                      <Input
                                        placeholder="Taşeron ara..."
                                        value={taseronSearchTerm}
                                        onChange={(e) => setTaseronSearchTerm(e.target.value)}
                                        className="pl-10"
                                      />
                                    </div>
                                  </div>
                                  <div className="max-h-[500px] overflow-y-auto">
                                    {taseronlar.filter(t => 
                                      t.name?.toLowerCase().includes(taseronSearchTerm.toLowerCase())
                                    ).length === 0 ? (
                                      <div className="p-4 text-center text-gray-500">
                                        {taseronSearchTerm ? 'Taşeron bulunamadı' : 'Bu şubede taşeron bulunmuyor'}
                                      </div>
                                    ) : (
                                      taseronlar
                                        .filter(t => t.name?.toLowerCase().includes(taseronSearchTerm.toLowerCase()))
                                        .map((taseron) => (
                                          <div
                                            key={taseron.id}
                                            onClick={() => handleTaseronSelect(taseron)}
                                            className={cn(
                                              "p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors",
                                              selectedTaseron?.id === taseron.id && "bg-orange-50 border-l-4 border-l-orange-500"
                                            )}
                                          >
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-2">
                                                <Building2 className={cn("h-4 w-4", selectedTaseron?.id === taseron.id ? "text-orange-600" : "text-gray-400")} />
                                                <span className="font-medium">{taseron.name}</span>
                                              </div>
                                              {taseron.malzeme_sayisi > 0 && (
                                                <Badge variant="outline" className="text-xs">
                                                  {taseron.malzeme_sayisi} malzeme
                                                </Badge>
                                              )}
                                            </div>
                                            {taseron.contact_person && (
                                              <div className="text-xs text-gray-500 mt-1 ml-6">
                                                {taseron.contact_person}
                                              </div>
                                            )}
                                          </div>
                                        ))
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Sağ Panel - Malzeme Listesi */}
                              <div className="lg:col-span-2">
                                {!selectedTaseron ? (
                                  <div className="border rounded-lg p-8 text-center text-gray-500 bg-gray-50">
                                    <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                    <p>Malzemeleri görüntülemek için sol taraftan bir taşeron seçin</p>
                                  </div>
                                ) : (
                                  <div className="border rounded-lg">
                                    <div className="p-3 bg-orange-50 border-b flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Building2 className="h-5 w-5 text-orange-600" />
                                        <span className="font-semibold text-orange-800">{selectedTaseron.name}</span>
                                        <Badge className="bg-orange-100 text-orange-700">
                                          {taseronMalzemeleri.length} kayıt
                                        </Badge>
                                      </div>
                                    </div>
                                    
                                    {taseronMalzemeLoading ? (
                                      <div className="p-8 text-center text-gray-500">Yükleniyor...</div>
                                    ) : taseronMalzemeleri.length === 0 ? (
                                      <div className="p-8 text-center text-gray-500">
                                        <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                        <p>Bu taşerona henüz malzeme verilmemiş</p>
                                      </div>
                                    ) : (
                                      <div className="max-h-[450px] overflow-y-auto">
                                        <Table>
                                          <TableHeader>
                                            <TableRow className="bg-gray-50">
                                              <TableHead>Tarih</TableHead>
                                              <TableHead>Ürün Adı</TableHead>
                                              <TableHead className="text-center">Miktar</TableHead>
                                              <TableHead>Teslim Alan</TableHead>
                                              <TableHead>Teslim Eden</TableHead>
                                              <TableHead>Belgeler</TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {taseronMalzemeleri.map((malzeme) => (
                                              <TableRow key={malzeme.id} className="hover:bg-gray-50">
                                                <TableCell className="text-sm">
                                                  <div>{new Date(malzeme.teslim_tarihi || malzeme.created_at).toLocaleDateString('tr-TR')}</div>
                                                  <div className="text-xs text-gray-400">
                                                    {new Date(malzeme.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                                  </div>
                                                </TableCell>
                                                <TableCell className="font-medium">{malzeme.urun_adi}</TableCell>
                                                <TableCell className="text-center">
                                                  <Badge variant="outline">
                                                    {malzeme.miktar} {malzeme.birim}
                                                  </Badge>
                                                </TableCell>
                                                <TableCell>{malzeme.teslim_alan || '-'}</TableCell>
                                                <TableCell className="text-sm text-gray-600">{malzeme.teslim_eden || malzeme.created_by_name}</TableCell>
                                                <TableCell>
                                                  {malzeme.belgeler && malzeme.belgeler.length > 0 ? (
                                                    <div className="flex gap-1">
                                                      {malzeme.belgeler.map((belge, idx) => (
                                                        <a key={idx} href={belge.data} target="_blank" rel="noopener noreferrer" title="Görüntüle">
                                                          <Eye className="h-4 w-4 text-blue-600 hover:text-blue-800" />
                                                        </a>
                                                      ))}
                                                    </div>
                                                  ) : (
                                                    <span className="text-gray-400">-</span>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* TESLİM ALANLAR TAB - Depo Paneli Yapısı (İki Panelli) */}
                    <TabsContent value="teslim-alanlar">
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-2">
                              <User className="h-5 w-5 text-purple-600" />
                              Teslim Alanlar ({teslimAlanlar.length} kişi)
                            </CardTitle>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Tüm teslim alan kayıtlarını topla
                                const allTeslimler = [];
                                teslimAlanlar.forEach(kisi => {
                                  if (kisi.cikislar && kisi.cikislar.length > 0) {
                                    kisi.cikislar.forEach(cikis => {
                                      cikis.items?.forEach(item => {
                                        allTeslimler.push({
                                          teslim_alan: kisi.ad,
                                          tarih: cikis.tarih || cikis.created_at,
                                          urun_adi: item.product_name || item.urun_adi || '-',
                                          miktar: `${item.quantity || item.miktar || 0} ${item.unit || item.birim || 'adet'}`,
                                          talep_eden: cikis.talep_eden || '-',
                                          teslim_eden: cikis.teslim_eden || '-',
                                          taseron_firma: cikis.taseron_firma || '-',
                                          aciklama: cikis.aciklama || '-'
                                        });
                                      });
                                    });
                                  }
                                });
                                
                                if (allTeslimler.length === 0) {
                                  toast.error('İndirilecek veri bulunmuyor');
                                  return;
                                }
                                
                                exportToExcel(allTeslimler, 'Teslim_Alanlar', [
                                  { key: 'teslim_alan', label: 'Teslim Alan' },
                                  { key: 'tarih', label: 'Tarih' },
                                  { key: 'urun_adi', label: 'Ürün Adı' },
                                  { key: 'miktar', label: 'Miktar' },
                                  { key: 'talep_eden', label: 'Talep Eden' },
                                  { key: 'teslim_eden', label: 'Teslim Eden' },
                                  { key: 'taseron_firma', label: 'Taşeron' },
                                  { key: 'aciklama', label: 'Açıklama' }
                                ]);
                              }}
                              className="text-purple-600 border-purple-300 hover:bg-purple-50"
                              data-testid="teslim-alanlar-excel-btn"
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Excel İndir
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {showTeslimAlanlarInfo && (
                            <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg relative">
                              <button
                                onClick={() => {
                                  setShowTeslimAlanlarInfo(false);
                                  localStorage.setItem('purchasing_teslim_alanlar_info_hidden', 'true');
                                }}
                                className="absolute top-2 right-2 p-1 hover:bg-purple-100 rounded"
                              >
                                <X className="h-4 w-4 text-purple-500" />
                              </button>
                              <div className="flex items-start gap-3">
                                <Info className="h-5 w-5 text-purple-600 mt-0.5" />
                                <div>
                                  <h4 className="font-medium text-purple-800">Teslim Alanlar Hakkında</h4>
                                  <p className="text-sm text-purple-600 mt-1">
                                    Bu bölümde stok çıkışlarında ürün teslim alan kişilerin listesi ve aldıkları malzemeler görüntülenir.
                                    Bir kişiye tıklayarak detaylı teslim geçmişini görebilirsiniz.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Arama */}
                          <div className="mb-4">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                placeholder="Teslim alan kişi ara..."
                                value={teslimAlanSearchTerm}
                                onChange={(e) => setTeslimAlanSearchTerm(e.target.value)}
                                className="pl-10"
                              />
                            </div>
                          </div>

                          {loading ? (
                            <div className="space-y-2">
                              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                              {/* Sol Panel - Teslim Alanlar Listesi */}
                              <div className="lg:col-span-1 border rounded-lg p-4 max-h-[600px] overflow-y-auto">
                                <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                                  <User className="h-4 w-4" />
                                  Kişiler
                                </h3>
                                {teslimAlanlar
                                  .filter(k => k.ad?.toLowerCase().includes(teslimAlanSearchTerm.toLowerCase()))
                                  .map((kisi) => (
                                    <div
                                      key={kisi.ad}
                                      onClick={() => handleTeslimAlanSelect(kisi)}
                                      className={cn(
                                        "p-3 rounded-lg cursor-pointer mb-2 transition-colors border",
                                        selectedTeslimAlan?.ad === kisi.ad
                                          ? "bg-purple-100 border-purple-300"
                                          : "hover:bg-gray-50 border-transparent"
                                      )}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center">
                                            <User className="h-4 w-4 text-purple-700" />
                                          </div>
                                          <div>
                                            <p className="font-medium text-gray-900">{kisi.ad || 'İsimsiz'}</p>
                                            <p className="text-xs text-gray-500">
                                              Son: {kisi.son_cikis_tarihi ? new Date(kisi.son_cikis_tarihi).toLocaleDateString('tr-TR') : '-'}
                                            </p>
                                          </div>
                                        </div>
                                        <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                                          {kisi.toplam_cikis} ürün
                                        </Badge>
                                      </div>
                                    </div>
                                  ))}
                                {teslimAlanlar.filter(k => k.ad?.toLowerCase().includes(teslimAlanSearchTerm.toLowerCase())).length === 0 && (
                                  <p className="text-gray-500 text-center py-4">
                                    {teslimAlanSearchTerm ? 'Kişi bulunamadı' : 'Bu şubede teslim alan kaydı bulunmuyor'}
                                  </p>
                                )}
                              </div>

                              {/* Sağ Panel - Teslim Detayları */}
                              <div className="lg:col-span-2 border rounded-lg p-4">
                                {selectedTeslimAlan ? (
                                  <div>
                                    <div className="flex items-center justify-between mb-4">
                                      <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center">
                                          <User className="h-6 w-6 text-purple-700" />
                                        </div>
                                        <div>
                                          <h3 className="font-bold text-lg text-gray-900">{selectedTeslimAlan.ad}</h3>
                                          <p className="text-sm text-gray-500">
                                            Toplam {selectedTeslimAlan.toplam_cikis} ürün teslim almış
                                          </p>
                                        </div>
                                      </div>
                                      <Badge variant="outline" className="text-purple-700 border-purple-300">
                                        {selectedTeslimAlan.cikislar?.length || 0} çıkış kaydı
                                      </Badge>
                                    </div>

                                    {/* Teslim Geçmişi Tablosu */}
                                    <div className="max-h-[450px] overflow-y-auto">
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Tarih</TableHead>
                                            <TableHead>Ürün</TableHead>
                                            <TableHead>Miktar</TableHead>
                                            <TableHead>Talep Eden</TableHead>
                                            <TableHead>Teslim Alan</TableHead>
                                            <TableHead>Teslim Eden</TableHead>
                                            <TableHead>Taşeron</TableHead>
                                            <TableHead>Açıklama</TableHead>
                                            <TableHead>Teslim Tutanağı</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {selectedTeslimAlan.cikislar?.flatMap((cikis, cikisIndex) => 
                                            cikis.items?.map((item, itemIndex) => (
                                              <TableRow key={`${cikisIndex}-${itemIndex}`}>
                                                <TableCell className="text-sm">
                                                  {cikis.tarih ? new Date(cikis.tarih).toLocaleString('tr-TR', {
                                                    day: '2-digit', month: '2-digit', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit'
                                                  }) : '-'}
                                                </TableCell>
                                                <TableCell className="font-medium">{item.product_name || item.urun_adi}</TableCell>
                                                <TableCell>
                                                  <Badge variant="outline">
                                                    {item.quantity || item.miktar} {item.unit || item.birim || 'adet'}
                                                  </Badge>
                                                </TableCell>
                                                <TableCell className="text-sm text-gray-600">{cikis.talep_eden || '-'}</TableCell>
                                                <TableCell className="text-sm text-gray-600">{cikis.teslim_alan || '-'}</TableCell>
                                                <TableCell className="text-sm text-gray-600">{cikis.teslim_eden || '-'}</TableCell>
                                                <TableCell className="text-sm text-gray-600">{cikis.taseron_firma || '-'}</TableCell>
                                                <TableCell className="text-sm text-gray-500 max-w-[150px] truncate">{cikis.aciklama || '-'}</TableCell>
                                                <TableCell>
                                                  {cikis.teslim_tutanaklari && cikis.teslim_tutanaklari.length > 0 ? (
                                                    <div className="space-y-1">
                                                      {cikis.teslim_tutanaklari.map((belge, bIndex) => {
                                                        let docUrl = belge.url || belge;
                                                        if (docUrl && !docUrl.startsWith('http')) {
                                                          docUrl = `${API_URL}${docUrl}`;
                                                        }
                                                        return (
                                                          <div key={bIndex} className="flex items-center gap-1 bg-green-50 border border-green-200 rounded px-2 py-1">
                                                            <FileText className="h-3 w-3 text-green-600" />
                                                            <span className="text-xs text-green-700 truncate max-w-[60px]" title={belge.name}>
                                                              {belge.name || 'Tutanak'}
                                                            </span>
                                                            {docUrl && (
                                                              <a 
                                                                href={docUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-500 hover:text-blue-700 ml-auto"
                                                                title="Görüntüle/İndir"
                                                              >
                                                                <Download className="h-3 w-3" />
                                                              </a>
                                                            )}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  ) : (
                                                    <span className="text-gray-400 text-xs">-</span>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            ))
                                          )}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center py-12 text-gray-500">
                                    <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                    <p>Teslim geçmişini görmek için sol taraftan bir kişi seçin</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Building className="h-16 w-16 mb-4 text-gray-300" />
              <p className="font-medium">Henüz şube tanımlanmamış</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ürün Giriş Geçmişi Modal */}
      <Dialog open={productHistoryModal} onOpenChange={setProductHistoryModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-600" />
              {selectedProductHistory.product_name} - Giriş Geçmişi
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Özet Bilgi */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-gray-500 text-xs">Toplam Giriş</Label>
                <p className="text-xl font-bold text-emerald-600">
                  {selectedProductHistory.total_quantity} {selectedProductHistory.unit}
                </p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Giriş Sayısı</Label>
                <p className="text-xl font-bold text-blue-600">{selectedProductHistory.entry_count}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Son Giriş</Label>
                <p className="text-sm font-medium">
                  {selectedProductHistory.last_entry_date ? formatDate(selectedProductHistory.last_entry_date) : '-'}
                </p>
              </div>
            </div>

            {/* Giriş Listesi */}
            <div>
              <Label className="text-gray-700 font-medium">Tüm Girişler</Label>
              <div className="mt-2 border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                {selectedProductHistory.entries?.map((entry, idx) => (
                  <div key={idx} className="p-3 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-emerald-600">
                          +{entry.quantity} {entry.unit}
                        </p>
                        <p className="text-sm text-gray-500">{formatDateTime(entry.date)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{entry.cari_hesap}</p>
                      </div>
                    </div>
                    {entry.description && (
                      <p className="text-xs text-gray-500 mt-2 italic">"{entry.description}"</p>
                    )}
                    {entry.documents?.length > 0 && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs text-gray-500 mb-1">Belgeler ({entry.documents.length}):</p>
                        <div className="flex flex-wrap gap-2">
                          {entry.documents.map((doc, docIdx) => (
                            <div key={docIdx} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
                              <span className="text-xs text-gray-700">{doc.name}</span>
                              {doc.data && (
                                <>
                                  <a href={doc.data} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 text-xs ml-1">
                                    Görüntüle
                                  </a>
                                  <span className="text-gray-300">|</span>
                                  <a href={doc.data} download={doc.name} className="text-emerald-600 hover:text-emerald-700 text-xs">
                                    İndir
                                  </a>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductHistoryModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hatalı Ürün Detay Modal */}
      <Dialog open={detailModal} onOpenChange={setDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Kayıt Detayı</DialogTitle>
          </DialogHeader>
          {detailData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Tarih</Label>
                  <p className="font-medium">{formatDate(detailData.created_at)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <p>{getDefectiveStatusBadge(detailData.status)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Ürün</Label>
                  <p className="font-medium">{detailData.product_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Hata Durumu</Label>
                  <p>{getIssueTypeBadge(detailData.issue_type)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Miktar</Label>
                  <p className="font-medium">{detailData.quantity} {detailData.unit}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Cari Hesap</Label>
                  <p className="font-medium">{detailData.cari_hesap || '-'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-500">Hata Çıkma Zamanı</Label>
                  <p className="font-medium">{getHataCikmaZamaniLabel(detailData.hata_cikma_zamani)}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-500">Açıklama</Label>
                  <p className="font-medium">{detailData.description || '-'}</p>
                </div>
                {detailData.resolution_notes && (
                  <div className="col-span-2">
                    <Label className="text-gray-500">Çözüm Notları</Label>
                    <p className="font-medium">{detailData.resolution_notes}</p>
                  </div>
                )}
              </div>

              {/* Belgeler */}
              {detailData.belgeler && detailData.belgeler.length > 0 && (
                <div>
                  <Label className="text-gray-500 mb-2 block">Belgeler ({detailData.belgeler.length})</Label>
                  <div className="space-y-2">
                    {detailData.belgeler.map((belge, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-sm">{belge.baslik || `Belge ${index + 1}`}</p>
                            <p className="text-xs text-gray-500">{belge.dosya_adi}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {belge.dosya && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => window.open(belge.dosya, '_blank')}>
                                <Eye className="h-4 w-4 mr-1" />
                                Görüntüle
                              </Button>
                              <a href={belge.dosya} download={belge.dosya_adi}>
                                <Button variant="outline" size="sm">
                                  <Download className="h-4 w-4 mr-1" />
                                  İndir
                                </Button>
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stok Ekle Modal */}
      <Dialog open={stokEkleModal} onOpenChange={setStokEkleModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5 text-emerald-600" />
              Stok Ekle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Şube Seçimi - En üstte */}
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <Label className="flex items-center gap-2 text-orange-700 font-medium">
                <Building className="h-4 w-4" />
                Hedef Şube *
              </Label>
              <Select
                value={stokEkleForm.target_branch_id}
                onValueChange={(val) => setStokEkleForm(prev => ({ ...prev, target_branch_id: val }))}
              >
                <SelectTrigger className="mt-2" data-testid="branch-select">
                  <SelectValue placeholder="Şube seçiniz..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tarih/Saat ve Tahmini Geliş Bilgileri */}
            <div className="grid grid-cols-2 gap-4">
              {/* Giriş Tarihi/Saati */}
              <div className="p-4 bg-gray-50 rounded-lg border">
                <Label className="flex items-center gap-2 text-gray-700 font-medium mb-3">
                  <Calendar className="h-4 w-4" />
                  Giriş Tarihi/Saati
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-gray-500">Tarih</Label>
                    <Input
                      type="date"
                      value={stokEkleForm.entry_date}
                      onChange={(e) => setStokEkleForm(prev => ({ ...prev, entry_date: e.target.value }))}
                      data-testid="entry-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Saat</Label>
                    <Input
                      type="time"
                      value={stokEkleForm.entry_time}
                      onChange={(e) => setStokEkleForm(prev => ({ ...prev, entry_time: e.target.value }))}
                      data-testid="entry-time"
                    />
                  </div>
                </div>
              </div>

              {/* Tahmini Depoya Geliş */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="flex items-center gap-2 text-blue-700 font-medium mb-3">
                  <Truck className="h-4 w-4" />
                  Tahmini Depoya Geliş *
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-blue-600">Tarih</Label>
                    <Input
                      type="date"
                      value={stokEkleForm.estimated_arrival_date}
                      onChange={(e) => setStokEkleForm(prev => ({ ...prev, estimated_arrival_date: e.target.value }))}
                      className={!stokEkleForm.estimated_arrival_date ? 'border-red-300' : ''}
                      data-testid="estimated-arrival-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-blue-600">Saat (opsiyonel)</Label>
                    <Input
                      type="time"
                      value={stokEkleForm.estimated_arrival_time}
                      onChange={(e) => setStokEkleForm(prev => ({ ...prev, estimated_arrival_time: e.target.value }))}
                      data-testid="estimated-arrival-time"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Cari Hesap Seçimi */}
            <div className="relative">
              <Label>Cari Hesap (Tedarikçi) *</Label>
              <Input
                value={cariSearchInput}
                onChange={(e) => {
                  setCariSearchInput(e.target.value);
                  setShowCariDropdown(true);
                }}
                onFocus={() => setShowCariDropdown(true)}
                placeholder="Cari hesap ara..."
                data-testid="cari-search-input"
              />
              {showCariDropdown && filteredCari.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredCari.slice(0, 10).map((cari) => (
                    <div
                      key={cari.id}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleSelectCari(cari)}
                    >
                      {cari.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Ürünler */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Ürünler *</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddItem}
                  className="gap-1"
                  data-testid="add-item-btn"
                >
                  <Plus className="h-4 w-4" />
                  Ürün Ekle
                </Button>
              </div>
              <div className="space-y-3">
                {stokEkleForm.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 relative">
                      <Label className="text-xs">Stok Kartı</Label>
                      <Input
                        value={stokSearchInputs[index] || ''}
                        onChange={(e) => {
                          handleStokSearch(index, e.target.value);
                          const newDropdowns = [...showStokDropdowns];
                          newDropdowns[index] = true;
                          setShowStokDropdowns(newDropdowns);
                        }}
                        onFocus={() => {
                          const newDropdowns = [...showStokDropdowns];
                          newDropdowns[index] = true;
                          setShowStokDropdowns(newDropdowns);
                        }}
                        placeholder="Ürün ara..."
                        data-testid={`stok-search-${index}`}
                      />
                      {showStokDropdowns[index] && filteredStokList[index]?.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {filteredStokList[index].slice(0, 10).map((stok) => (
                            <div
                              key={stok.id}
                              className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                              onClick={() => handleSelectStok(index, stok)}
                            >
                              <div className="font-medium">{stok.product_name}</div>
                              <div className="text-xs text-gray-500">Birim: {stok.unit || 'adet'}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="w-20">
                      <Label className="text-xs">Miktar</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemQuantityChange(index, e.target.value)}
                        placeholder="0"
                        data-testid={`quantity-${index}`}
                      />
                    </div>
                    <div className="w-16">
                      <Label className="text-xs">Birim</Label>
                      <Input
                        value={item.unit}
                        disabled
                        className="bg-gray-100 text-center"
                      />
                    </div>
                    <div className="w-28">
                      <Label className="text-xs text-emerald-600">Alış Fiyatı (₺)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.purchase_price}
                        onChange={(e) => handleItemPriceChange(index, e.target.value)}
                        placeholder="0.00"
                        className="text-right"
                        data-testid={`purchase-price-${index}`}
                      />
                    </div>
                    {stokEkleForm.items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(index)}
                        className="mt-5 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Açıklama */}
            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={stokEkleForm.description}
                onChange={(e) => setStokEkleForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Stok girişi hakkında not..."
                rows={2}
                data-testid="stok-description"
              />
            </div>

            {/* Belgeler - Zorunlu */}
            <div>
              <Label className="flex items-center gap-1">
                Belgeler <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500">(En az 1 belge zorunlu)</span>
              </Label>
              <div className={`border-2 border-dashed rounded-lg p-4 mt-2 ${stokEkleForm.documents.length === 0 ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}>
                <input
                  type="file"
                  multiple
                  onChange={handleStokFileChange}
                  className="hidden"
                  id="stok-files"
                />
                <label htmlFor="stok-files" className="flex flex-col items-center cursor-pointer">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Dosya yüklemek için tıklayın</span>
                </label>
              </div>
              {stokEkleForm.documents.length > 0 && (
                <div className="space-y-2 mt-2">
                  {stokEkleForm.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveStokFile(index)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setStokEkleModal(false)}>İptal</Button>
            <Button 
              onClick={handleSubmitStokEkle} 
              disabled={stokEkleSubmitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-stok-ekle"
            >
              {stokEkleSubmitting ? 'Ekleniyor...' : 'Stok Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zimmet Geçmişi Modal */}
      <Dialog open={showGecmisModal} onOpenChange={setShowGecmisModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-orange-600" />
              Zimmet Geçmişi - {selectedZimmetGecmis?.urun_adi}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedZimmetGecmis && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-gray-500 text-xs">Ürün Adı</Label>
                  <p className="font-medium">{selectedZimmetGecmis.urun_adi}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Marka / Model</Label>
                  <p className="font-medium">{selectedZimmetGecmis.marka || '-'} / {selectedZimmetGecmis.model || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Seri No</Label>
                  <p className="font-mono">{selectedZimmetGecmis.seri_no || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Mevcut Durum</Label>
                  <div className="mt-1">{getDurumBadge(selectedZimmetGecmis.durum)}</div>
                </div>
              </div>
            )}

            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <History className="h-4 w-4" />
                Hareket Geçmişi
              </h4>
              {zimmetGecmisi.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Henüz hareket kaydı bulunmuyor</p>
              ) : (
                <div className="space-y-2">
                  {zimmetGecmisi.map((hareket, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-1">
                        <Clock className="h-4 w-4 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{hareket.islem_tipi}</span>
                          <span className="text-sm text-gray-500">
                            {new Date(hareket.tarih).toLocaleString('tr-TR')}
                          </span>
                        </div>
                        {hareket.kullanici_adi && (
                          <p className="text-sm text-blue-600">
                            <UserCheck className="h-3 w-3 inline mr-1" />
                            {hareket.kullanici_adi}
                          </p>
                        )}
                        {hareket.aciklama && (
                          <p className="text-sm text-gray-600 mt-1">{hareket.aciklama}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">İşlemi yapan: {hareket.islem_yapan}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-orange-600" />
              Depolar Arası Transfer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Kaynak ve Hedef Şube Seçimi */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kaynak Depo (Nereden)</Label>
                <Select 
                  value={transferForm.source_branch_id} 
                  onValueChange={handleSourceBranchChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kaynak depo seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(branch => (
                      <SelectItem 
                        key={branch.id} 
                        value={branch.id}
                        disabled={branch.id === transferForm.destination_branch_id}
                      >
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Hedef Depo (Nereye)</Label>
                <Select 
                  value={transferForm.destination_branch_id} 
                  onValueChange={(v) => setTransferForm(prev => ({ ...prev, destination_branch_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Hedef depo seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(branch => (
                      <SelectItem 
                        key={branch.id} 
                        value={branch.id}
                        disabled={branch.id === transferForm.source_branch_id}
                      >
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ürün Seçimi */}
            {transferForm.source_branch_id && (
              <div className="space-y-3">
                <Label>Ürün Seçin (Kaynak Depodaki Stok)</Label>
                {sourceStockLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : sourceStock.length === 0 ? (
                  <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                    <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p>Kaynak depoda transfer edilebilir stok bulunmuyor</p>
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ürün Adı</TableHead>
                          <TableHead className="text-center">Mevcut Miktar</TableHead>
                          <TableHead className="text-right">İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sourceStock.map((stock) => (
                          <TableRow key={stock.id || stock.stok_karti_adi}>
                            <TableCell className="font-medium">{stock.stok_karti_adi}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">{stock.miktar} {stock.birim}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAddTransferItem(stock)}
                                disabled={selectedTransferItems.find(i => i.product_name === stock.stok_karti_adi)}
                                className="gap-1"
                              >
                                <Plus className="h-3 w-3" />
                                Ekle
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {/* Seçili Ürünler */}
            {selectedTransferItems.length > 0 && (
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-orange-600" />
                  Transfer Edilecek Ürünler ({selectedTransferItems.length})
                </Label>
                <div className="border rounded-lg p-3 bg-orange-50">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ürün</TableHead>
                        <TableHead className="text-center">Mevcut</TableHead>
                        <TableHead className="text-center">Transfer Miktarı</TableHead>
                        <TableHead className="text-right">Kaldır</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedTransferItems.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{item.product_name}</TableCell>
                          <TableCell className="text-center text-gray-500">
                            {item.available_quantity} {item.unit}
                          </TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="number"
                              min={1}
                              max={item.available_quantity}
                              value={item.quantity}
                              onChange={(e) => handleTransferItemQuantityChange(index, e.target.value)}
                              className="w-24 mx-auto text-center"
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveTransferItem(index)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Açıklama */}
            <div className="space-y-2">
              <Label>Açıklama (İsteğe bağlı)</Label>
              <Textarea
                value={transferForm.description}
                onChange={(e) => setTransferForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Transfer hakkında not ekleyebilirsiniz..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSubmitTransfer}
              disabled={transferSubmitting || !transferForm.source_branch_id || !transferForm.destination_branch_id || selectedTransferItems.length === 0}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {transferSubmitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Transfer Ediliyor...
                </>
              ) : (
                <>
                  <ArrowLeftRight className="h-4 w-4 mr-2" />
                  Transferi Tamamla
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Geçmişi Modal */}
      <Dialog open={showTransferHistoryModal} onOpenChange={setShowTransferHistoryModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Transfer Geçmişi
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {transfersLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : transfers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ArrowLeftRight className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Henüz transfer kaydı bulunmuyor</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>Kaynak Depo</TableHead>
                    <TableHead className="text-center">→</TableHead>
                    <TableHead>Hedef Depo</TableHead>
                    <TableHead>Ürünler</TableHead>
                    <TableHead>Belgeler</TableHead>
                    <TableHead>Açıklama</TableHead>
                    <TableHead>İşlemi Yapan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transfers.map((transfer) => (
                    <TableRow key={transfer.id}>
                      <TableCell className="text-sm">
                        <div>{new Date(transfer.created_at).toLocaleDateString('tr-TR')}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(transfer.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          {transfer.source_branch_name}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <ArrowLeftRight className="h-4 w-4 text-gray-400 mx-auto" />
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {transfer.destination_branch_name}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {transfer.items?.map((item, idx) => (
                            <div key={idx} className="text-sm">
                              <span className="font-medium">{item.product_name}</span>
                              <Badge variant="secondary" className="ml-2 text-xs">
                                {item.quantity} {item.unit}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {transfer.teslim_alan_belgesi && (
                            <a
                              href={`${API_URL}${transfer.teslim_alan_belgesi.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              <Download className="h-3 w-3" />
                              Teslim Alan
                            </a>
                          )}
                          {transfer.teslim_eden_belgesi && (
                            <a
                              href={`${API_URL}${transfer.teslim_eden_belgesi.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 hover:underline"
                            >
                              <Download className="h-3 w-3" />
                              Teslim Eden
                            </a>
                          )}
                          {!transfer.teslim_alan_belgesi && !transfer.teslim_eden_belgesi && (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500 max-w-[150px] truncate">
                        {transfer.description || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {transfer.created_by_name}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasingWarehouses;
